import { data } from 'autoprefixer';
import React, { act, useMemo } from 'react';
import {
  XAxis,
  YAxis,
  ZAxis,
  CartesianGrid,
  Scatter,
  ComposedChart,
  ResponsiveContainer,
  Bar,
  Tooltip
} from 'recharts';

// Custom components for the box plot
const HorizonBar = (props) => {
  const { x, y, width, height } = props;
  if (x == null || y == null || width == null || height == null) {
    return null;
  }
  return (
    <line x1={x} y1={y} x2={x+width} y2={y} stroke="#000" strokeWidth={3} />
  );
};

const DotBar = (props) => {
  const { x, y, width, height } = props;
  if (x == null || y == null || width == null || height == null) {
    return null; 
  }
  return (
    <line 
      x1={x + width/2}
      y1={y + height}
      x2={x + width/2}
      y2={y}
      stroke={"#000"}
      strokeWidth={2}
      strokeDasharray={"8"} // Fixed: camelCase in JSX
    />
  );
};

// Box plot data transformation hook
const useBoxPlot = (boxPlots) => {
  const data = useMemo(
    () =>
      boxPlots.map((v) => {
        return {
          name: v.name,
          min: v.min,
          bottomWhisker: v["25%"] - v.min,
          bottomBox: v['50%'] - v[`25%`],
          topBox: v['75%'] - v['50%'],
          topWhisker: v.max - v['75%'],
          average: v.mean,
          size: 500
        };
      }),
    [boxPlots]
  );

  return data;
};
const formatCurrency = (value) => {
  if (value === null || value === undefined || isNaN(parseFloat(value))){
    return "0.00";
  }
  return parseFloat(value).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
}
// Custom Tooltip
const CustomTooltip = ({ active, payload, label, rawData, selectedMetric }) => {
  if (active && payload && payload.length) {
    const rawItem = rawData.find(item => item.name === label);
    if (rawItem){
      // Check selected metric
      const isPecentageMetric = selectedMetric === 'Margin' || selectedMetric === 'Spread';

      // format function based on metric type 
      const formatValue = (value) => {
        if(isPecentageMetric){
          if(value === null || value === undefined || isNaN(parseFloat(value))) {
            return "0.00%";
          }
          return parseFloat(value).toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          }) + "%";
        }else{
          return "MYR " + formatCurrency(value);
        }
      }
      return (
        <div className="custom-tooltip bg-white p-2 rounded-md shadow-md">
          <p className="label p-2 font-bold text-lg flex flex-col items-center">{`${label}`}</p>
          <div className='grid grid-cols-2'>
            <p className='label text-start font-medium'>Minimum</p>
            <p className="label">: {formatValue(rawItem.min)}</p>

            <p className='label text-left font-medium'>Quantile 1</p>
            <p className="label text-start">: {formatValue(rawItem['25%'])}</p>

            <p className='label text-left font-medium'>Median</p>
            <p className="label text-start">: {formatValue(rawItem['50%'])}</p>

            <p className='label text-left font-medium'>Quantile 3</p>
            <p className="label text-start">: {formatValue(rawItem['75%'])}</p>

            <p className='label text-left font-medium'>Max </p>
            <p className="label text-start">:  {formatValue(rawItem.max)}</p>

            {rawItem.mean !== undefined &&(
              <>
              <p className='label text-left font-medium'>Average </p>
              <p className="label text-start">:  {formatValue(rawItem.mean)}</p>
              </>
            )}
          </div>
        </div>
      );
    }
  }
  return null;
}
const CustomerKPIComparisonGraph = ({ selectedMetric, boxPlotsData }) => {
    // Transform the box plot data for the chart
    // console.log(`Selected metric: ${selectedMetric}`);
    // console.table(boxPlotsData);
    const dataToBeDisplayed = [];
    // Add data points if they exists
    if (boxPlotsData) {
      for (let i = 0; i < 3; i++){
        if (boxPlotsData[i]){
          dataToBeDisplayed.push({
            name: boxPlotsData[i]?.name || `${selectedMetric} ${i+1}`,
            min: boxPlotsData[i]?.min,
            '25%': boxPlotsData[i]?.['25%'],
            '50%': boxPlotsData[i]?.['50%'],
            '75%': boxPlotsData[i]?.['75%'],
            max: boxPlotsData[i]?.max,
            mean: boxPlotsData[i]?.mean,
          })
        }
      }
    }
    // console.table(dataToBeDisplayed);
    const data = useBoxPlot(dataToBeDisplayed);
    return (
      <div className='p-2 w-full h-full flex flex-col items-center justify-center'>
          {/* <h2>Customer KPI Comparison Graph</h2> */}
          <ResponsiveContainer minHeight={600} width="100%">
            <ComposedChart
              data={data}
              margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
              barSize={250} // Control the size of the bar
            >
              <CartesianGrid strokeDasharray="3 3" />
              <Bar stackId={"a"} dataKey={"min"} fill={"none"} />
              <Bar stackId={"a"} dataKey={"bar"} shape={<HorizonBar />} />
              <Bar stackId={"a"} dataKey={"bottomWhisker"} shape={<DotBar />} />
              <Bar stackId={"a"} dataKey={"bottomBox"} fill={"#FEFAD8"} />
              <Bar stackId={"a"} dataKey={"bar"} shape={<HorizonBar />} />
              <Bar stackId={"a"} dataKey={"topBox"} fill={"#FEFAD8"} />
              <Bar stackId={"a"} dataKey={"topWhisker"} shape={<DotBar />} />
              <Bar stackId={"a"} dataKey={"bar"} shape={<HorizonBar />} />
              {/* Size of the Average Dot */}
              <ZAxis type="number" dataKey="size" range={[0, 100]} />
              <Scatter dataKey="average" fill={"#F7E21E"} stroke={"#000"} />
              <XAxis
                dataKey='name'
                label={{ value: 'Comparison', position: 'insideBottom', offset: -5 }}
                tick={{ fontSize: 12 }} />
              <YAxis
                label={{ angle: -90, position: 'insideLeft', style: {textAnchor:'middle'} }}
                tickFormatter={(value) => {
                  if(value >= 1000000){
                    return `${(value / 1000000).toFixed(1)}M`;
                  } else if (value >= 1000){
                    return `${(value / 1000).toFixed(0)}K`;
                  } else {
                    return value;
                  }
                }} />
              <Tooltip content={<CustomTooltip rawData={dataToBeDisplayed}  selectedMetric={selectedMetric}/>} />
              {/* <Tooltip /> */}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
    );
};

export default CustomerKPIComparisonGraph;